import { useTranslation } from 'react-i18next'
import { AlertTriangle, Gauge } from 'lucide-react'
import { useEntitlements } from '@/hooks/use-entitlements'
import { cn } from '@/lib/utils'

/** Quota counter: lifetime on the FREE plan, pooled for the school and
 *  scoped to the current subscription period on paid plans. Renders
 *  nothing for unlimited plans (or while loading). */
export function QuotaBanner({ className }: { className?: string }) {
  const { t } = useTranslation()
  const { data: ent } = useEntitlements()
  if (!ent || ent.quota == null) return null

  const remaining = ent.assessments_remaining ?? 0
  const exhausted = remaining <= 0
  const isFree = ent.plan === 'FREE'

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-3 rounded-lg border text-sm',
        exhausted
          ? 'bg-rosso-light border-rosso/30 text-rosso-dark'
          : 'bg-mim-lightblue border-mim-blue/30 text-mim-blue dark:bg-primary/10',
        className,
      )}
    >
      {exhausted ? (
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
      ) : (
        <Gauge className="w-4 h-4 mt-0.5 flex-shrink-0" />
      )}
      <div>
        <p className="font-medium">
          {t(isFree ? 'quota.titleFree' : 'quota.titleSchool', {
            used: ent.assessments_used, quota: ent.quota,
          })}
        </p>
        <p className="text-xs mt-0.5 opacity-90">
          {exhausted
            ? t(isFree ? 'quota.exhaustedFree' : 'quota.exhaustedSchool')
            : t('quota.remaining', { count: remaining })}
        </p>
      </div>
    </div>
  )
}

export default QuotaBanner
